import * as React from 'react';
import { captureError } from '@leagueplatform/observability';
import { JsonEditorValue } from '@web-config-app/core-react-ui';
import {
  useRulesTemplates,
  useRuleTemplatesEditorStateStoreActions,
} from '@web-config-app/core-react';
import type {
  AnnotatedJsonSchema,
  RulesTemplateWithSchema,
} from '@web-config-app/core';
import {
  useEntityDetailsProps,
  replacePlaceholderInRules,
  getRulesTemplateWithSchema,
} from '@web-config-app/entity-form';
import { decodeJsonLogicString } from './decode-json-logic-string';
import type { RuleTemplateFormData } from './rule-template-form/rule-template-form.component';

// Only the completionCriteria control has the downstream publish guard
// (`assertCompletionCriteriaPublishable`). Other rulesTemplateType values
// (rewardCriteria, eligibilityCriteria, branchingRule) rely on the older
// "empty resolve clears the field" behavior — their services treat `null`
// and `{}` differently (reward crediting errors on `{}` but is permissive
// on nil), so we keep the pre-EF-951 clear semantics for them.
const COMPLETION_CRITERIA_RULES_TYPE = 'completionCriteria';

interface UseJsonLogicControlProps {
  handleChange: (path: string, value: any) => void;
  path: string;
  schema: AnnotatedJsonSchema;
  data?: string;
  parentFieldPath?: string;
  isArrayItem?: boolean;
  arrayIndex?: number;
}

const getRulesTemplateType = (schema: AnnotatedJsonSchema) =>
  schema?.['x-entity-control']?.options?.jsonLogic?.rulesTemplateType;

export const useJsonLogicControl = ({
  data,
  handleChange,
  path,
  schema,
  parentFieldPath,
  isArrayItem,
  arrayIndex,
}: UseJsonLogicControlProps) => {
  const { entityType, formPath } = useEntityDetailsProps();
  const rulesType = getRulesTemplateType(schema);
  const jsonLogicFieldPath = `${formPath}.${path}`;

  // If this is a rule combination control - use the regular form path, if this is a child single rule - we want it to be the parent path
  const topLevelFieldPath = parentFieldPath || jsonLogicFieldPath;

  // If this is a rule combination control, there will be no nestedFieldPath
  const nestedFieldPath = parentFieldPath ? jsonLogicFieldPath : undefined;

  const controlType = parentFieldPath ? 'singleRule' : 'ruleCombination';

  // If its a single rule, we want the spacing to make the rule formatted nice in the json editor
  const jsonSpacing = controlType === 'singleRule' ? 2 : undefined;

  const { getRuleTemplateFieldData, setRuleTemplateFieldData } =
    useRuleTemplatesEditorStateStoreActions();

  const ruleTemplateFieldData = getRuleTemplateFieldData({
    fieldPath: topLevelFieldPath,
    nestedFieldPath,
    arrayIndex,
  });

  const rulesTemplates = useRulesTemplates({
    entityType,
    rulesType,
    controlType,
  });

  const dataTemplateId = ruleTemplateFieldData?.rulesTemplate?.templateId;

  const dataRuleTemplate = rulesTemplates?.find(
    (template) => template.id === dataTemplateId,
  );

  const [jsonContent, setJsonContent] = React.useState<JsonEditorValue>({
    json: undefined,
    text: decodeJsonLogicString(data),
  });
  const [selectedRuleTemplate, setSelectedRuleTemplate] = React.useState<
    RulesTemplateWithSchema | undefined
  >(() => {
    // if there is not a dataRuleTemplateId but data exists in the ruleCombination control - we want to set the ruleTemplate to be 'single_rule_condition' to be able to display the json-logic data that was saved without a template (likely because it was created on the old BE)
    if (!dataTemplateId && controlType === 'ruleCombination' && data) {
      return getRulesTemplateWithSchema({
        selectedRuleTemplate: rulesTemplates?.find(
          (template) => template.id === 'single_rule_condition',
        ),
        parentLabelKey: schema['x-entity-label']?.key,
      });
    }
    return getRulesTemplateWithSchema({
      selectedRuleTemplate: dataRuleTemplate,
      parentLabelKey: schema['x-entity-label']?.key,
    });
  });

  const handleOnJsonChange = React.useCallback(
    (content: JsonEditorValue) => {
      setJsonContent(content);
      // Preserve the pre-EF-951 "'{}' clears to undefined" behavior for
      // non-completionCriteria rules (reward/eligibility/branching): their
      // downstream services treat `null` as permissive and error on `{}`,
      // so silently persisting the string would create the opposite
      // failure mode we're fixing. The publish guard only covers
      // completionCriteria, so only that rule type needs the retained-
      // criteria behavior.
      const shouldRetainEmptyForGuard =
        rulesType === COMPLETION_CRITERIA_RULES_TYPE && !!selectedRuleTemplate;
      if (
        controlType === 'ruleCombination' &&
        content.text === '{}' &&
        !shouldRetainEmptyForGuard
      ) {
        handleChange(path, undefined);
        return;
      }
      handleChange(path, content.text);
    },

    [handleChange, path, controlType, rulesType, selectedRuleTemplate],
  );

  const [ruleTemplateFormData, setRuleTemplateFormData] =
    React.useState<RuleTemplateFormData>(() => {
      // if there is no dataTemplateId from editorState but data exists in the ruleCombination control - we want to set the ruleCombination to be the incoming data to be able to display the json-logic data that was saved without a template (likely because it was created on the old BE)
      if (!dataTemplateId && controlType === 'ruleCombination' && data) {
        return { ruleCombination: data };
      }
      return ruleTemplateFieldData?.rulesTemplate?.templateValues;
    });

  const handleRuleTemplateFormDataChange = (formData: RuleTemplateFormData) => {
    setRuleTemplateFormData(formData);

    if (selectedRuleTemplate && formData) {
      // Try the resolve first. If it fails, we neither update
      // editorState nor the field — otherwise editorState would advance
      // (new templateValues) while the field still shows the previous
      // resolve, and publish would ship the stale rule while the guard
      // sees a non-empty payload.
      const resolveResult = replacePlaceholderInRules(
        selectedRuleTemplate.rules,
        formData,
        controlType === 'ruleCombination',
      );
      if (resolveResult.status === 'incomplete') {
        // Non-empty missingKeys is the normal state of a form mid-fill
        // (JSONForms fires onChange per keystroke, so every intermediate
        // state on a multi-input template resolves to `incomplete`).
        // Suppress the signal for that case so the anomalous paths —
        // nullish input rules and the util's catch-all — stay diagnostic
        // and don't get buried under routine authoring traffic.
        if (resolveResult.missingKeys.length === 0) {
          captureError(
            new Error(
              'Rule template resolver returned incomplete; skipping write',
            ),
            {
              errorName: 'RuleTemplateResolverIncompleteSkipWrite',
              context: {
                RuleTemplateResolver: {
                  rulesType,
                  controlType,
                  jsonLogicFieldPath,
                  templateId: selectedRuleTemplate.id,
                },
              },
            },
          );
        }
        return;
      }
      setRuleTemplateFieldData({
        fieldPath: topLevelFieldPath,
        nestedFieldPath,
        isArrayItem,
        arrayIndex,
        ruleTemplateFieldData: {
          rulesTemplate: {
            rulesMode: 'template',
            templateId: selectedRuleTemplate?.id,
            templateValues: formData,
          },
        },
      });
      handleOnJsonChange({
        json: undefined,
        text: JSON.stringify(resolveResult.value, null, jsonSpacing),
      });
    }
  };

  const handleTemplateChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    const templateValue = event.target.value;

    const selectedTemplate = rulesTemplates?.find(
      (template) => template.id === templateValue,
    );

    const hasPreviouslySelectedArrayCombination = Array.isArray(
      ruleTemplateFieldData?.rulesTemplate?.templateValues?.ruleCombination,
    );
    const selectedTemplateIsArrayCombination =
      selectedTemplate?.inputs?.ruleCombination?.type === 'array';

    // We retain the data in nested fields when switching between rule combinations
    // that contain arrays of rules.
    const shouldResetNestedFields = selectedTemplateIsArrayCombination
      ? !hasPreviouslySelectedArrayCombination
      : !parentFieldPath;

    const isArraySwitchPath =
      !shouldResetNestedFields &&
      controlType === 'ruleCombination' &&
      !!selectedTemplate;

    // The array-switch branch is different from the mid-edit case in
    // `handleRuleTemplateFormDataChange`: the switch itself is the user's
    // deliberate intent (they just picked a new template), so
    // `setRuleTemplateFieldData` below must land even when the resolver
    // can't compute a new rule yet — the previous rule shape rarely
    // matches the new template's placeholders, so `incomplete` is the
    // expected outcome. On incomplete we leave `arraySwitchResolvedRule`
    // undefined; the fallback below writes the template's raw rules to
    // the field, matching the non-array template-selection path.
    let arraySwitchResolvedRule: unknown;
    if (isArraySwitchPath) {
      const arraySwitchResolveResult = replacePlaceholderInRules(
        selectedTemplate!.rules,
        typeof data === 'string' ? JSON.parse(data) : data,
        controlType === 'ruleCombination',
      );
      if (arraySwitchResolveResult.status === 'complete') {
        arraySwitchResolvedRule = arraySwitchResolveResult.value;
      } else if (arraySwitchResolveResult.missingKeys.length === 0) {
        // Only report the anomalous incompletes (nullish rules / util
        // catch-all) — missing placeholder keys at switch time are the
        // norm, not a signal worth surfacing.
        captureError(
          new Error(
            'Rule template resolver returned incomplete on template switch',
          ),
          {
            errorName: 'RuleTemplateSwitchResolverIncompleteSkipWrite',
            context: {
              RuleTemplateSwitch: {
                rulesType,
                controlType,
                jsonLogicFieldPath,
                templateId: selectedTemplate!.id,
              },
            },
          },
        );
      }
    }

    setRuleTemplateFieldData({
      nestedFieldPath,
      shouldResetNestedFields,
      fieldPath: topLevelFieldPath,
      isArrayItem,
      arrayIndex,
      ruleTemplateFieldData: {
        rulesTemplate: {
          rulesMode: 'template',
          templateId: selectedTemplate?.id,
          ...(shouldResetNestedFields
            ? undefined
            : { templateValues: ruleTemplateFormData }),
        },
      },
    });

    setSelectedRuleTemplate(
      getRulesTemplateWithSchema({
        selectedRuleTemplate: selectedTemplate,
        parentLabelKey: schema['x-entity-label']?.key,
      }),
    );

    if (isArraySwitchPath) {
      // The user has switched between two templates that use array combinations. We need
      // to manually update the data with the new combination operand. Fall back to the
      // template's raw rules on incomplete resolve — writing `'{}'` here would trip
      // `handleOnJsonChange`'s clearing branch for non-completionCriteria rulesTypes
      // (their `shouldRetainEmptyForGuard` is false), silently wiping the field with
      // no downstream guard to catch it. Raw rules keep the field non-empty and match
      // the non-array template-selection path a few lines below; on completionCriteria,
      // `assertCompletionCriteriaPublishable`'s `UNRESOLVED_PLACEHOLDER_PATTERN` still
      // blocks publish.
      handleOnJsonChange({
        json: undefined,
        text: JSON.stringify(
          arraySwitchResolvedRule ?? selectedTemplate!.rules ?? {},
          null,
          jsonSpacing,
        ),
      });
    } else {
      // ruleTemplateFormData must always be an object instead of undefined to support our input validation - this way the errors via EntityForm / JsonForms are always tied to specific inputs not "data must be an object"
      setRuleTemplateFormData({});

      if (!selectedTemplate) {
        // Switching back to "no template": clear the field directly.
        // handleOnJsonChange's clear branch can't be relied on here because
        // its closure still holds the previous `selectedRuleTemplate`
        // (setSelectedRuleTemplate above is async), so its `!selectedRuleTemplate`
        // guard would misfire and persist the string '{}'.
        setJsonContent({ json: undefined, text: '{}' });
        handleChange(path, undefined);
        return;
      }

      handleOnJsonChange({
        json: undefined,
        text: JSON.stringify(selectedTemplate.rules ?? {}, null, jsonSpacing),
      });
    }
  };

  return {
    rulesTemplates,
    rulesType,
    jsonContent,
    jsonLogicFieldPath,
    selectedRuleTemplate,
    ruleTemplateFormData,
    handleOnJsonChange,
    handleRuleTemplateFormDataChange,
    handleTemplateChange,
  };
};
